// FlatHashMapFast.ts
/**
 * 高性能 HashMap，使用BitMask64Data作为Key。内部计算两层哈希：
 *  - primaryHash: MurmurHash3(seed1) => 定位 bucket
 *  - secondaryHash: MurmurHash3(seed2) => 处理 bucket 内碰撞判定
 *
 *  理论上，在1e5数量数据规模下碰撞概率在数学意义上的可忽略。
 *  在本地测试中，一千万次连续/随机BitMask64Data生成未发生一级哈希冲突，考虑到使用场景（原型系统、组件系统等）远达不到此数量级，因此可安全用于生产环境。
 */
export class BitMaskHashMap {
    constructor() {
        this.buckets = new Map();
        this._size = 0;
    }
    get size() {
        return this._size;
    }
    get innerBuckets() {
        return this.buckets;
    }
    /** MurmurHash3 (32bit) 简化实现 */
    murmur32(key, seed) {
        let h = seed >>> 0;
        const mix = (k) => {
            k = Math.imul(k, 0xcc9e2d51) >>> 0; // 第一个 32 位魔术常数
            k = (k << 15) | (k >>> 17);
            k = Math.imul(k, 0x1b873593) >>> 0; // 第二个 32 位魔术常数
            h ^= k;
            h = (h << 13) | (h >>> 19);
            h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
        };
        // base
        mix(key.base[0] >>> 0);
        mix(key.base[1] >>> 0);
        // segments
        if (key.segments) {
            for (const seg of key.segments) {
                mix(seg[0] >>> 0);
                mix(seg[1] >>> 0);
            }
        }
        h ^= (key.segments ? key.segments.length * 8 : 8);
        h ^= h >>> 16;
        h = Math.imul(h, 0x85ebca6b) >>> 0;
        h ^= h >>> 13;
        h = Math.imul(h, 0xc2b2ae35) >>> 0;
        h ^= h >>> 16;
        return h >>> 0;
    }
    /** primaryHash + secondaryHash 计算 */
    getHashes(key) {
        const primary = this.murmur32(key, 0x9747b28c); // seed1
        const secondary = this.murmur32(key, 0x12345678); // seed2
        return [primary, secondary];
    }
    set(key, value) {
        const [primary, secondary] = this.getHashes(key);
        let bucket = this.buckets.get(primary);
        if (!bucket) {
            bucket = [];
            this.buckets.set(primary, bucket);
        }
        // 查找是否存在 secondaryHash
        for (let i = 0; i < bucket.length; i++) {
            if (bucket[i][0] === secondary) {
                bucket[i][1] = value;
                return this;
            }
        }
        // 新增
        bucket.push([secondary, value]);
        this._size++;
        return this;
    }
    get(key) {
        const [primary, secondary] = this.getHashes(key);
        const bucket = this.buckets.get(primary);
        if (!bucket)
            return undefined;
        for (let i = 0; i < bucket.length; i++) {
            if (bucket[i][0] === secondary) {
                return bucket[i][1];
            }
        }
        return undefined;
    }
    has(key) {
        return this.get(key) !== undefined;
    }
    delete(key) {
        const [primary, secondary] = this.getHashes(key);
        const bucket = this.buckets.get(primary);
        if (!bucket)
            return false;
        for (let i = 0; i < bucket.length; i++) {
            if (bucket[i][0] === secondary) {
                bucket.splice(i, 1);
                this._size--;
                if (bucket.length === 0) {
                    this.buckets.delete(primary);
                }
                return true;
            }
        }
        return false;
    }
    clear() {
        this.buckets.clear();
        this._size = 0;
    }
    *entries() {
        for (const [_, bucket] of this.buckets) {
            for (const [_secondary, value] of bucket) {
                // 无法还原原始 key（只存二级 hash），所以 entries 返回不了 key
                yield [undefined, value];
            }
        }
    }
    *values() {
        for (const bucket of this.buckets.values()) {
            for (const [_, value] of bucket) {
                yield value;
            }
        }
    }
}
//# sourceMappingURL=BitMaskHashMap.js.map